import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Column } from 'features/Yucollect/utils/types';
import {
  IFetchCampaignsByAgentRequest,
  IFetchCampaignsByAgentResponse,
  ISaveCampaignRequest,
  ISaveCampaignResponse,
} from 'redux/types/CRM.types';
import { IFailedPayload } from 'redux/types/Call.types';
import {
  BorrowersResponse,
  FetchBorrowersPayload,
} from 'redux/types/SupervisorDashboard.types';

export const TASK_TYPES = {
  ALL_TASK: 'ALL_TASK',
  MY_TASK: 'MY_TASK',
  LIVE_CALLS: 'LIVE_CALLS',
} as const;

export type TaskType = (typeof TASK_TYPES)[keyof typeof TASK_TYPES];

export interface SearchBorrowersPayload extends FetchBorrowersPayload {
  searchValue: string;
}

export interface DispositionSummary {
  name: string;
  borrowerCount: number;
  TOS: number;
  id: any;
  percentage?: number;
}

export interface DispositionSummaryResponse {
  dispositions: DispositionSummary[];
}

export interface SupervisorDashboardState {
  loading: boolean;
  borrowers: BorrowersResponse;
  error: string | null;
  borrowersError: IFailedPayload | null;
  activeTab: TaskType;
  dispositionSummary: DispositionSummaryResponse;
  dispositionSummaryLoading: boolean;
  campaigns: IFetchCampaignsByAgentResponse[];
  saveCampaignCompleted: boolean;
}

const defaultBorrower: BorrowersResponse = {
  data: [],
  totalRecords: null,
  pagination: {
    limit: 10,
    offset: 0,
    page: 0,
    totalCount: 0,
    pageSize: 0,
  },
  allocation_id: '',
  allocation_file_name: null,
  allocation_start_date: null,
  allocation_end_date: null,
};

export const supervisorDashboardInitState: SupervisorDashboardState = {
  loading: false,
  borrowers: defaultBorrower,
  error: null,
  activeTab: TASK_TYPES.ALL_TASK,
  dispositionSummary: {
    dispositions: [],
  },
  dispositionSummaryLoading: false,
  campaigns: [],
  borrowersError: null,
  saveCampaignCompleted: false,
};

const SupervisorDashboardSlice = createSlice({
  name: 'supervisorDashboard',
  initialState: supervisorDashboardInitState,
  reducers: {
    fetchBorrowersRequest(state, action: PayloadAction<FetchBorrowersPayload>) {
      state.loading = true;
    },
    fetchBorrowersSuccess(state, action: PayloadAction<BorrowersResponse>) {
      state.loading = false;
      state.borrowersError = null; // Clear error on success
      state.borrowers = action.payload;
    },
    fetchBorrowersFailure(state, action: PayloadAction<IFailedPayload>) {
      state.loading = false;
      state.borrowersError = action.payload;
      state.borrowers = defaultBorrower;
    },
    searchBorrowersRequest(
      state,
      action: PayloadAction<SearchBorrowersPayload>
    ) {
      state.loading = true;
      state.error = null;
    },
    searchBorrowersSuccess(state, action: PayloadAction<BorrowersResponse>) {
      state.loading = false;
      state.borrowers = action.payload;
    },
    searchBorrowersFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
    setActiveTab(state, action: PayloadAction<TaskType>) {
      state.activeTab = action.payload;
    },
    setPagination(
      state,
      action: PayloadAction<{ offset: number; limit: number }>
    ) {
      state.borrowers.pagination.offset = action.payload.offset;
      state.borrowers.pagination.limit = action.payload.limit;
    },
    fetchDispositionSummaryRequest(
      state,
      action: PayloadAction<{
        agentId: string;
        campaignId: string;
        limit?: number;
        offset?: number;
        taskType: TaskType;
        columns: Column[];
        dispositionIds: string[];
      }>
    ) {
      state.dispositionSummaryLoading = true;
      state.error = null;
    },
    fetchDispositionSummarySuccess(
      state,
      action: PayloadAction<DispositionSummaryResponse>
    ) {
      state.dispositionSummaryLoading = false;
      state.dispositionSummary = action.payload;
    },
    fetchDispositionSummaryFailure(state, action: PayloadAction<string>) {
      state.dispositionSummaryLoading = false;
      state.error = action.payload;
    },
    fetchCampaignsByAgent: (
      state,
      action: PayloadAction<IFetchCampaignsByAgentRequest>
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchCampaignsByAgentSuccess: (
      state,
      action: PayloadAction<IFetchCampaignsByAgentResponse[]>
    ) => {
      state.loading = false;
      state.campaigns = action.payload;
    },
    fetchCampaignsByAgentFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    saveCampaign: (state, action: PayloadAction<ISaveCampaignRequest>) => {
      state.loading = true;
      state.error = null;
      state.saveCampaignCompleted = false;
    },
    saveCampaignSuccess: (
      state,
      action: PayloadAction<ISaveCampaignResponse>
    ) => {
      state.loading = false;
      state.saveCampaignCompleted = true;
    },
    saveCampaignFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
      state.saveCampaignCompleted = true;
    },
    resetSaveCampaignCompleted: (state) => {
      state.saveCampaignCompleted = false;
    },
  },
});

export const {
  fetchBorrowersRequest,
  fetchBorrowersSuccess,
  fetchBorrowersFailure,
  searchBorrowersRequest,
  searchBorrowersSuccess,
  searchBorrowersFailure,
  setActiveTab,
  setPagination,
  fetchDispositionSummaryRequest,
  fetchDispositionSummarySuccess,
  fetchDispositionSummaryFailure,
  fetchCampaignsByAgent,
  fetchCampaignsByAgentSuccess,
  fetchCampaignsByAgentFailure,
  saveCampaign,
  saveCampaignSuccess,
  saveCampaignFailure,
  resetSaveCampaignCompleted,
} = SupervisorDashboardSlice.actions;

export default SupervisorDashboardSlice.reducer;
